import React from 'react';
import { RocketLaunchIcon, LightBulbIcon, DocumentTextIcon, ShareIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

export default function Stage3() {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 3: Learn From It + Make Required Long Term Adjustments"
        icon={null}
        heading="Extract learnings and implement long-term changes"
      >
        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">TL;DR</h2>
          <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
            <ul className="list-decimal pl-6 space-y-1">
              <li>Unlock all the learning possible to avoid this happening again.</li>
              <li>Make changes today that will mitigate this event repeating</li>
            </ul>
            <p className="mt-4">Here's what we mean…</p>
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Unlock all the learning possible to avoid this happening again.</h2>
          <div className="mb-4">
            <p className="mb-2 font-semibold">Explore Every Angle:</p>
            <p className="mb-4">Get the team together—not just the leadership or those involved, but everyone. The goal is to share freely what we can learn from this. Everyone's perspective can shed light on different aspects.</p>
            <p className="mb-2 font-semibold">Document it:</p>
            <p className="mb-4">Keep a shared, live document (e.g. a notion page or miro board) where everyone can drop their thoughts and insights anytime.</p>
          </div>
          <ExpandableCallout type="tip" title="Async is best here" defaultOpen={true}>
            <p>Async is best here as it avoids dominant personalities crowding the mike – Notion/Miro equivalents are your friends.</p>
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Action</h2>
          <p className="mb-4 font-semibold">Create A Teachable Moment For The Whole Organisation:</p>
          <p className="mb-4">Summarise the key learnings and make a decision on what can be actioned.</p>
          <ul className="space-y-2 text-gray-300 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Publish the key themes and update on when you deliver on that promise</span>
            </li>
          </ul>
          <CopyToNotion title="Team Retro Recap Example">
            <pre className="bg-gray-900 rounded p-4 text-sm overflow-x-auto">
{`Team,

Thanks everyone that contributed to the Open Retro {notion} on [issue].

Here's a recap on our key learnings as a business:

- learning #1
- learning #2
- learning #3

As a result, we are going to commit to changing the following over the 
next [insert feasible timeline ]

- action #1
- action #2

Thanks for bearing with us on this.

Best,
[your name]`}
            </pre>
          </CopyToNotion>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Go further: be open.</h2>
          <p className="mb-4">You've just learned through something hard. This is a time to celebrate. Not only that, it is an incredible opportunity to help others benefit from your learnings and build trust in your employer brand.</p>
          <p className="mb-4">How? Well you've already published your learnings for the team. It's not a lot of work to repurpose that into something more public.</p>
          <p className="mb-4 font-semibold">Example:</p>
          <ExpandableCallout type="tip" title="Posthog Example" defaultOpen={true}>
            <a
              href="https://posthog.com/newsletter/mistakes-weve-made-at-posthog"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary"
            >
              Posthog published some of their big mistakes and what they learned from them.
            </a>
          </ExpandableCallout>
          <p className="mt-4">This kind of content loop is exactly how you build an authentic employer brand. It doesn't have to just be when f*ck ups happen, think of it as a pattern for any time you make a change in your ways of working, aka your culture.</p>
          
          <PlaybookImage
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/we-fucked-up/Untitled.png"
            alt="Visual representation of building authentic employer brand through content loops"
            caption="Building trust and authenticity through transparent communication about learnings and changes"
          />
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
} 